import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Avatar from '@material-ui/core/Avatar';
import makeStyles from '@material-ui/core/styles/makeStyles';

import axiosInstance from '../../utils/axiosInstance';
import endpoints from '../../utils/endpoints';

const GREEN = '#4EA14C';
const DARK_GREEN = '#20441E';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    minHeight: '100vh',
    margin: 0,
    padding: 0,
    backgroundColor: '#081A1B',
    backgroundImage:
      'radial-gradient(circle at top right, #204F51 0%, #081A1B 80%)',
    color: '#fff',
    boxSizing: 'border-box',
    overflow: 'auto',
  },
  content: {
    maxWidth: 600,
    margin: '0 auto',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    boxSizing: 'border-box',
  },
  section: {
    marginBottom: theme.spacing(2),
    color: '#fff',
  },
  textSecondary: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  storeButton: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    backgroundColor: GREEN,
    backgroundImage: `linear-gradient(135deg, ${GREEN} 0%, ${DARK_GREEN} 100%)`,
    color: '#fff',
    borderRadius: 28,
    '&:hover': {
      backgroundColor: GREEN,
      backgroundImage: `linear-gradient(135deg, ${GREEN} 0%, ${DARK_GREEN} 100%)`,
      filter: 'brightness(0.95)',
    },
  },
  clientCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid rgba(255, 255, 255, 0.12)`,
  },
  clientAvatar: {
    width: 48,
    height: 48,
    backgroundColor: GREEN,
    backgroundImage: `linear-gradient(135deg, ${GREEN} 0%, ${DARK_GREEN} 100%)`,
    color: '#fff',
    fontSize: '1.25rem',
  },
  clientName: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '1rem',
  },
  appIcon: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    objectFit: 'cover',
    marginBottom: theme.spacing(1.5),
    display: 'block',
  },
  appName: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '1.5rem',
    marginBottom: theme.spacing(3),
  },
}));

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/** Link preview (Open Graph / Telegram / etc.) — keep in sync with defaults in public/index.html */
const INVITE_SHARE_META = {
  'lt-LT': {
    siteName: 'Vietinis Tyrinėtojas',
    title:
      'Išbandžiau bombinę kelionių programėlę su virtualiu gidu! Rekomenduoju ir Tau!',
    description:
      'Pamirškite kelionių planavimą! Programėlėje rasite jau paruoštas keliones su virtualiu gidu, kuris rodys kur važiuoti, pasakos vietovių istorijas ir rekomenduos kur pavalgyti ar apsistoti.',
  },
  'en-EU': {
    siteName: 'Vietinis Tyrinėtojas',
    title:
      'I tried an awesome travel app with a virtual guide! I recommend it to you too!',
    description:
      'Forget fussing over trip planning — the app includes ready-made routes with a virtual guide that shows where to drive, tells local stories, and suggests places to eat or stay.',
  },
};

const INVITE_PAGE_TEXTS = {
  'lt-LT': {
    appName: 'Vietinis Tyrinėtojas',
    appIconAlt: 'Programos piktograma',
    userLabel: 'Naudotojas #',
    invitedTitle:
      'Jus pakvietė išbandyti kelionių programėlę "Vietinis Tyrinėtojas"!',
    getAppTitle: 'Atsisiųskite programėlę',
    invitedDescription:
      'Pamirškite kelionių planavimą! Programėlėje rasite jau paruoštas keliones su virtualiu gidu, kuris rodys kur važiuoti, pasakos vietovių istorijas ir rekomenduos kur pavalgyti ar apsistoti.',
    couldNotLoadLinks: 'Nepavyko įkelti nuorodų į programėlę.',
    downloadOn: 'Atsisiųskite',
    storeLinksNotConfigured: 'Parduotuvių nuorodos nesukonfigūruotos.',
  },
  'en-EU': {
    appName: 'Vietinis Tyrinėtojas',
    appIconAlt: 'App icon',
    userLabel: 'User #',
    invitedTitle: 'You were invited to try the app',
    getAppTitle: 'Get the app',
    invitedDescription:
      'Download the app, create an account, and accept the invite – the person who invited you will receive a reward.',
    couldNotLoadLinks: 'Could not load app links.',
    downloadOn: 'Download on',
    storeLinksNotConfigured: 'Store links are not configured.',
  },
};

const InviteLandingPage = () => {
  const classes = useStyles();
  const location = useLocation();
  const locale = location.pathname.startsWith('/en-EU/') ? 'en-EU' : 'lt-LT';
  const t = INVITE_PAGE_TEXTS[locale] || INVITE_PAGE_TEXTS['en-EU'];
  const share = INVITE_SHARE_META[locale] || INVITE_SHARE_META['lt-LT'];
  const query = useQuery();
  const code = (query.get('code') || '').trim();

  const [inviterAccount, setInviterAccount] = useState(null);
  const [storeUrls, setStoreUrls] = useState({ android: null, ios: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const displayName = (inviterAccount?.fullName || '').trim() || null;
  const inviterAvatarUrl = inviterAccount?.profileImageUrl
    ? inviterAccount.profileImageUrl.startsWith('http')
      ? inviterAccount.profileImageUrl
      : `${axiosInstance.defaults.baseURL || ''}${inviterAccount.profileImageUrl}`
    : null;
  const inviterInitials = displayName
    ? displayName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0])
        .join('')
        .toUpperCase()
    : code
      ? String(code).slice(0, 2).toUpperCase()
      : '?';

  const ogImageUrl = useMemo(() => {
    if (typeof window === 'undefined') return '/social.png';
    return `${window.location.origin}/social.png`;
  }, []);

  const ogPageUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}${location.pathname}${location.search}`;
  }, [location.pathname, location.search]);

  // Fetch inviter display info when code is present
  useEffect(() => {
    if (!code) {
      setInviterAccount(null);
      return;
    }
    let cancelled = false;
    axiosInstance
      .get(endpoints.accountInfoByUserId(code))
      .then(({ data }) => {
        if (!cancelled && data) {
          setInviterAccount({
            fullName: data.fullName || null,
            profileImageUrl: data.profileImageUrl || null,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setInviterAccount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  // Fetch store URLs from app-version (Url_Android_App, Url_iOS_App) – one request, no platform
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axiosInstance.get(endpoints.appVersion);
        if (cancelled) return;
        const raw = res?.data?.data ?? res?.data ?? {};
        const android = raw.Url_Android_App ?? raw.url ?? null;
        const ios = raw.Url_iOS_App ?? raw.url ?? null;
        setStoreUrls({
          android:
            android && String(android).trim() ? String(android).trim() : null,
          ios: ios && String(ios).trim() ? String(ios).trim() : null,
        });
      } catch (err) {
        if (!cancelled) setError(t.couldNotLoadLinks);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const prevBodyBg = document.body.style.backgroundColor;
    const prevBodyMargin = document.body.style.margin;
    const prevBodyPadding = document.body.style.padding;
    const prevHtmlBg = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = '#081A1B';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.backgroundColor = '#081A1B';
    return () => {
      document.body.style.backgroundColor = prevBodyBg;
      document.body.style.margin = prevBodyMargin;
      document.body.style.padding = prevBodyPadding;
      document.documentElement.style.backgroundColor = prevHtmlBg;
    };
  }, []);

  const helmet = (
    <Helmet>
      <html lang={locale === 'lt-LT' ? 'lt' : 'en'} />
      <title>{share.title}</title>
      <meta name="description" content={share.description} />
      <meta property="og:title" content={share.title} />
      <meta property="og:description" content={share.description} />
      <meta property="og:site_name" content={share.siteName} />
      <meta property="og:type" content="website" />
      {ogPageUrl ? <meta property="og:url" content={ogPageUrl} /> : null}
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1069" />
      <meta property="og:image:height" content="1069" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={share.title} />
      <meta name="twitter:description" content={share.description} />
      <meta name="twitter:image" content={ogImageUrl} />
    </Helmet>
  );

  if (loading) {
    return (
      <>
        {helmet}
        <Box
          className={classes.root}
          display="flex"
          justifyContent="center"
          alignItems="center"
          p={3}
        >
          <CircularProgress style={{ color: GREEN }} />
        </Box>
      </>
    );
  }

  return (
    <Box className={classes.root}>
      {helmet}
      <Box className={classes.content}>
        <img src="/icon.png" alt={t.appIconAlt} className={classes.appIcon} />
        <Typography className={classes.appName}>{t.appName}</Typography>
        {code && (
          <Box className={classes.clientCard}>
            <Avatar
              src={inviterAvatarUrl || undefined}
              className={classes.clientAvatar}
            >
              {!inviterAvatarUrl ? inviterInitials : null}
            </Avatar>
            <Typography className={classes.clientName}>
              {displayName || `${t.userLabel}${code}`}
            </Typography>
          </Box>
        )}

        <Typography variant="h5" className={classes.section}>
          {code ? t.invitedTitle : t.getAppTitle}
        </Typography>
        {code && (
          <Typography variant="body2" className={classes.textSecondary}>
            {t.invitedDescription}
          </Typography>
        )}

        {error && (
          <Typography color="error" className={classes.section}>
            {error}
          </Typography>
        )}

        <Box className={classes.section}>
          <Typography variant="subtitle2" className={classes.textSecondary}>
            {t.downloadOn}
          </Typography>
          {storeUrls.android && (
            <Button
              className={classes.storeButton}
              variant="contained"
              component="a"
              href={storeUrls.android}
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Play
            </Button>
          )}
          {storeUrls.ios && (
            <Button
              className={classes.storeButton}
              variant="contained"
              component="a"
              href={storeUrls.ios}
              target="_blank"
              rel="noopener noreferrer"
            >
              App Store
            </Button>
          )}
          {!storeUrls.android && !storeUrls.ios && !loading && (
            <Typography variant="body2" className={classes.textSecondary}>
              {t.storeLinksNotConfigured}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default InviteLandingPage;
